// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth } from '../firebase'; // Your initialized Firebase auth instance
import { onAuthStateChanged } from 'firebase/auth';

// Create the context
const AuthContext = createContext();

// Custom hook to use the AuthContext easily
export function useAuth() {
  return useContext(AuthContext);
}

// Provider component definition
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userOrgId, setUserOrgId] = useState(null);
  const [userFidClaim, setUserFidClaim] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true); // Loading state for auth check

  useEffect(() => {
    // Listen for authentication state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user); // Set the user object (or null)

      if (user) {
        // If user is logged in, get their ID token result to access custom claims
        try {
          // Force refresh true? Optional, maybe only needed on initial load/login elsewhere
          const idTokenResult = await user.getIdTokenResult(false);
          const claims = idTokenResult.claims;
          console.log("Auth Provider Claims:", claims); // Log claims centrally

          // Set claims state (using the check for string 'true' as discussed)
          setIsAdmin(claims.admin === 'true');
          setUserOrgId(claims.orgId || null);
          setUserFidClaim(claims.userFid || null);

          if (!claims.orgId) {
              console.warn("Auth Provider: User is missing orgId claim.");
              // Decide if this is an error state for the whole app
          }

        } catch (error) {
          console.error("Auth Provider: Error fetching user claims:", error);
          // Handle error state if needed, maybe clear claims
          setIsAdmin(false);
          setUserOrgId(null);
          setUserFidClaim(null);
          // Optionally set a global error state here
        }
      } else {
        // No user, clear claims state
        setIsAdmin(false);
        setUserOrgId(null);
        setUserFidClaim(null);
      }
      setLoadingAuth(false); // Mark auth check as complete
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []); // Empty dependency array means this runs once on mount

  // Value object provided to consuming components
  const value = {
    currentUser,
    isAdmin,
    userOrgId,
    userFidClaim,
    loadingAuth, // Provide loading state
  };

  // Render the provider with the value and children
  // Don't render children until initial auth check is done
  return (
    <AuthContext.Provider value={value}>
      {!loadingAuth && children}
    </AuthContext.Provider>
  );
}